import React from 'react'
import { Alert } from 'react-bootstrap'
import SelectIterationModal from '../../../modals/SelectIterationModal'
import { useAppDispatch } from '../../../../store/hooks'
import { setCompareModelId } from '../../../../store/slices/modelSlice'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import { selectSelectedModelId, selectCompareModelId } from '../../../../store/slices/modelSlice'
import { useFetchModelsQuery } from '../../../../services/modelsApi'
import styled from 'styled-components'
import { ModelComparisonCore } from '../../ModelComparisonView'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { FaExclamationTriangle, FaCompressArrowsAlt, FaInfoCircle } from 'react-icons/fa'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
`

const CompareHeader = styled.div`
  background: linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%);
  border-bottom: 1px solid rgba(147, 51, 234, 0.2);
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`

const CompareTitle = styled.h2`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const CompareIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.9rem;
`

const CompareDescription = styled.p`
  color: #b0b0b0;
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.4;
`

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #1a1a1a;
  overflow: visible;
  height: 100%;
  padding-top: 1rem;
`

const ComparePhase: React.FC = () => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const compareModelId = useAppSelector(selectCompareModelId)

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  const selectedModel = modelsData?.models?.find((m) => m.id === selectedModelId)
  const compareModel = modelsData?.models?.find((m) => m.id === compareModelId)
  const [showSelectModal, setShowSelectModal] = React.useState(false)

  // Check if we have both models for comparison
  const hasBothModels = selectedModel && compareModel
  const hasModels = modelsData?.models && modelsData.models.length > 0
  const hasMultipleModels = modelsData?.models && modelsData.models.length > 1

  return (
    <Container>
      {/* <CompareHeader> */}
      {/* <div>
          <CompareTitle>
            <CompareIcon>
              <FaCompressArrowsAlt />
            </CompareIcon>
            Compare Model Performance
          </CompareTitle>
          <CompareDescription>
            Side-by-side comparison of model metrics, training data, and real-time inference results
            to make informed decisions.
          </CompareDescription>
        </div> */}

      {/* Status indicators */}
      {!hasModels && (
        <Alert
          variant="warning"
          className="d-flex align-items-center gap-2"
          style={{
            margin: 0,
            background: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '8px',
            padding: '0.75rem',
          }}
        >
          <FaExclamationTriangle />
          <div>
            <strong>No Models Available</strong>
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
              Create at least one model to use comparison features.
            </div>
          </div>
        </Alert>
      )}

      {hasModels && !hasMultipleModels && (
        <Alert
          variant="info"
          className="d-flex align-items-center gap-2"
          style={{
            margin: 0,
            background: 'rgba(147, 51, 234, 0.1)',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            borderRadius: '8px',
            padding: '0.75rem',
          }}
        >
          <FaInfoCircle />
          <div>
            <strong>Single Model</strong>
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
              Create additional models to enable side-by-side comparisons.
            </div>
          </div>
        </Alert>
      )}

      {hasMultipleModels && !hasBothModels && (
        <Alert
          variant="info"
          className="d-flex align-items-center gap-2"
          style={{
            margin: 0,
            background: 'rgba(147, 51, 234, 0.1)',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            borderRadius: '8px',
            padding: '0.75rem',
          }}
        >
          <FaInfoCircle />
          <div>
            <strong>Select Comparison Model</strong>
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
              Choose a second model from the dropdown to begin comparison.
            </div>
          </div>
        </Alert>
      )}
      {/* </CompareHeader> */}

      <ContentContainer>
        <ModelComparisonCore
          project={project}
          model1={selectedModel}
          model2={compareModel}
          initialTab="performance"
          onRequestSelectModel2={() => setShowSelectModal(true)}
          onError={(msg) => {
            // Surface errors in console for now; can integrate ErrorDialog if needed
            // eslint-disable-next-line no-console
            console.error(msg)
          }}
        />
      </ContentContainer>

      <SelectIterationModal
        show={showSelectModal}
        onHide={() => setShowSelectModal(false)}
        onIterationSelect={(model) => {
          dispatch(setCompareModelId(model.id))
          setShowSelectModal(false)
        }}
        childModels={(modelsData?.models || []).filter((m) => m.parent_id === selectedModelId)}
        selectedIterationId={compareModelId}
        baseModelName={selectedModel?.name || 'Base Model'}
        project={project}
      />
    </Container>
  )
}

export default ComparePhase
